import { createContext, type ReactNode, useContext, useState } from 'react';
import { X } from 'lucide-react';
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useCardService } from '@/services/card.service';
import type { Card } from '@/types/financial-workspace';
import CloseCardModal from './close-card.modal';
import RenameCardModal from './rename-card.modal';

export const WalletCardModalType = {
  CloseCard: 'close-card',
  RenameCard: 'rename-card',
} as const;

type WalletCardModalName = (typeof WalletCardModalType)[keyof typeof WalletCardModalType];

type WalletCardModalData = {
  cardId?: string;
  cardName: string;
  card?: Card;
  onRename?: (card: Card) => void;
};

type WalletCardModalContextType = {
  openModal: (modalName: WalletCardModalName, data: WalletCardModalData) => void;
  closeModal: () => void;
};

const WalletCardModalContext = createContext<WalletCardModalContextType | undefined>(undefined);

export const useWalletCardModal = () => {
  const context = useContext(WalletCardModalContext);
  if (!context) {
    throw new Error('useWalletCardModal must be used within a WalletCardModalProvider');
  }
  return context;
};

export function WalletCardModalProvider({ children }: { children: ReactNode }) {
  const { closeCard, renameCard } = useCardService();
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [currentModal, setCurrentModal] = useState<{ modalName: WalletCardModalName; data: WalletCardModalData }>();

  const openModal = (modalName: WalletCardModalName, data: WalletCardModalData) => {
    setCurrentModal({ modalName, data });
    setOpen(true);
  };

  const closeModal = () => {
    if (closing || renaming) return;
    setCurrentModal(undefined);
    setOpen(false);
  };

  const handleCloseCard = async () => {
    const cardId = currentModal?.data.cardId;
    if (!cardId || closing) return;

    setClosing(true);
    try {
      const closedCard = await closeCard(cardId, 'Closed by card manager');
      if (closedCard) {
        setCurrentModal(undefined);
        setOpen(false);
      }
    } finally {
      setClosing(false);
    }
  };

  const handleRenameCard = async (name: string) => {
    const card = currentModal?.data.card;
    if (!card || renaming) return;

    setRenaming(true);
    try {
      const updatedCard = await renameCard(card, name);
      if (updatedCard) {
        currentModal.data.onRename?.(updatedCard);
        setCurrentModal(undefined);
        setOpen(false);
      }
    } finally {
      setRenaming(false);
    }
  };

  return (
    <WalletCardModalContext.Provider value={{ openModal, closeModal }}>
      {children}
      <Dialog open={open} onOpenChange={(nextOpen) => (!nextOpen ? closeModal() : setOpen(nextOpen))}>
        <DialogContent className="w-[calc(100%-3.2rem)] max-w-[50rem] !rounded-[1.4rem] border-primary bg-secondary px-[2.4rem] pb-[2.4rem] pt-[2.8rem] sm:px-[3.2rem]" onOpenAutoFocus={(event) => event.preventDefault()}>
          <DialogClose asChild>
            <Button type="button" variant="ghost" size="icon" disabled={closing || renaming} aria-label="Close modal" className="absolute right-[1.2rem] top-[1.2rem] h-[3.2rem] w-[3.2rem] text-main hover:text-second">
              <X className="h-[1.8rem] w-[1.8rem]" />
            </Button>
          </DialogClose>
          {currentModal?.modalName === WalletCardModalType.CloseCard && currentModal.data.cardId && (
            <CloseCardModal
              cardName={currentModal.data.cardName}
              closing={closing}
              onCancel={closeModal}
              onConfirm={() => void handleCloseCard()}
            />
          )}
          {currentModal?.modalName === WalletCardModalType.RenameCard && (
            <RenameCardModal
              cardName={currentModal.data.cardName}
              saving={renaming}
              onCancel={closeModal}
              onConfirm={(name) => void handleRenameCard(name)}
            />
          )}
        </DialogContent>
      </Dialog>
    </WalletCardModalContext.Provider>
  );
}
